<template>
  <div />
</template>

<script>
import paths from 'routes/paths'
import store from 'store'
import Rooms from 'api/Rooms'
import { findDayAfterToday, parseDate, formatDate02, addDay } from 'utilities/date'
import { mapGetters } from 'vuex'
import {
  buildKidsAges,
  buildKidsTiers,
  buildRooms,
  genAPIParamsForRooms,
  genKidsAgesTransferPayload
} from 'utilities/generateSearchForm'
import _isEmpty from 'lodash/isEmpty'
import _isNil from 'lodash/isNil'
import Hotels from 'api/Hotels'

export default {
  name: 'RoomsPreloader',

  beforeRouteEnter(to, _from, next) {
    const isApplyingAfterCoupon =
      store.getters['booking/isApplyingAfterCoupon'] || to?.query?.after_coupon
    const searchForm = store.getters['booking/getSearchForm']
    const confirmationNumber = searchForm?.current_reservation || to?.query?.current_reservation
    const bypass_token = searchForm?.bypass_token || to?.query?.bypass_token
    const email = searchForm?.email || to?.query?.email
    if (isApplyingAfterCoupon && confirmationNumber) {
      store.dispatch('booking/setUpdateMode', false)
      next({
        name: paths.reservation.review,
        params: { confirmationNumber },
        query: { bypass_token, email, after_coupon: null }
      })
    } else {
      next()
    }
  },

  data() {
    return {
      initLoading: true,
      loading: true,
      facilitiesSuggestion: [],
      isLoadingSuggestion: false,
      warning: this.$route.query.warning || null
    }
  },

  computed: {
    ...mapGetters({
      searchForm: 'search/getSearchForm',
      filters: 'search/getSearchFormFilters',
      dayUseSearchActive: 'setting/showDayUse',
      isUndated: 'search/isUndated',
      isVoucherMode: 'search/isVoucherMode',
      formattedAges: 'search/getFormattedAges',
      initialSetting: 'setting/getInit',
      guest: 'booking/getGuest',
      limitedPlans: 'booking/getLimitedPlans',
      defaultSearchType: 'setting/getSearchType',
      isTiersMode: 'setting/isTiersMode',
      isInitAPIArrived: 'search/isInitAPIArrived',
      defaultSearch: 'setting/getDefautSearch',
      isSharedBedKidsMode: 'setting/showSharedBedKids',
      siteController: 'setting/getSiteController',
      bookAvailableRoomsOnlyDefaultSetting: 'setting/bookAvailableRoomsOnlyDefaultSetting',
      hotelId: 'setting/getHotelId',
      nearbyHotelsSuggestionEnabled: 'setting/nearbyHotelsSuggestionEnabled',
      gtmDataLayerSettings: 'setting/getGtmDataLayer',
      hotelCode: 'setting/getHotelCode',
      currentUser: 'membership/getCurrentUser',
      nights: 'search/getNights',
      isSignedIn: 'membership/isSignedIn',
      isMultiRoomSearchEnabled: 'setting/isMultiRoomSearchEnabled',
      brandNotifications: 'booking/getBrandNotifications',
      metasearchSource: 'setting/getMetasearchSource'
    })
  },

  created() {
    this.init()
  },

  methods: {
    search(searchAttr) {
      return this.callSearch(searchAttr)
    },
    callSearch(searchAttr) {
      const searchParamsForAPI = genAPIParamsForRooms(
        { ...searchAttr },
        this.isTiersMode,
        this.isSharedBedKidsMode
      )

      if (this.$route?.query?.type === 'plan') {
        return this.searchPlans(searchParamsForAPI)
      } else {
        return this.searchRooms(searchParamsForAPI)
      }
    },
    searchRooms(searchParamsForAPI) {
      return Rooms.get({ ...searchParamsForAPI, type: 'rooms' })
        .then((roomData) => {
          roomData.rooms = roomData.rooms.map((room) => {
            return {
              ...room,
              room_plan: room.room_plan.map((plan) => ({
                ...plan,
                has_brr: !!plan['non_sign_in_discount'].applied_scrape_id
              }))
            }
          })

          this.$store.dispatch('search/setPreloadRoomsResult', roomData)
          return roomData
        })
        .catch(() => {})
    },
    searchPlans(searchParamsForAPI) {
      return Rooms.get({ ...searchParamsForAPI, type: 'plan' })
        .then((planData) => {
          planData.plans = planData.plans.map((plan) => {
            return {
              ...plan,
              rooms: plan.rooms.map((room) => ({
                ...room,
                has_brr: !!room['non_sign_in_discount'].applied_scrape_id
              }))
            }
          })
          planData.rooms = planData.plans

          this.$store.dispatch('search/setPreloadPlansResult', planData)
          return planData
        })
        .catch(() => {})
    },
    assignQueryToSearchForm() {
      return new Promise((resolve) => {
        const {
          adults,
          checkin,
          checkout,
          children,
          type = this.defaultSearchType,
          room_type_code: roomTypeCode,
          hotel_plan_code: hotelPlanCode,
          room_plan_code: roomPlanCode,
          mode,
          promo_code,
          order = this.defaultSearch?.sort_order,
          is_including_occupied,
          kids_ages: kidsAges,
          coupon_code,
          room_count,
          utm_source,
          utm_medium,
          utm_campaign,
          medium,
          source,
          email_id,
          search_type,
          nights,
          price_displayed_total,
          google_site,
          google_promo_code,
          currency
        } = this.$route.query
        let {
          room_type_codes: roomTypeCodes,
          hotel_plan_codes: hotelPlanCodes,
          room_plan_codes: roomPlanCodes,
          is_day_use: isDayUse,
          filter
        } = this.$route.query
        const kidsAgesList = buildKidsAges(kidsAges)
        roomTypeCodes = _isEmpty(roomTypeCodes) ? [] : roomTypeCodes?.split(',')
        hotelPlanCodes = _isEmpty(hotelPlanCodes) ? [] : hotelPlanCodes?.split(',')
        roomPlanCodes = _isEmpty(roomPlanCodes) ? [] : roomPlanCodes?.split(',')
        isDayUse = isDayUse === 'true'

        // We support 2 syntaxes for filters
        // ?filter=1&filter=2&filter=3
        // or ?filter=1,2,3
        if (!_isEmpty(filter) && !Array.isArray(filter) && filter.includes(',')) {
          filter = filter.split(',')
        }

        // Force filter to be an array since there maybe only 1 filter in the URL query
        if (!Array.isArray(filter) && !!filter) {
          filter = [filter]
        }

        // If both room_type_codes and room_type_code are present in the URL, take room_type_codes
        if (_isEmpty(roomTypeCodes) && roomTypeCode) {
          roomTypeCodes = [roomTypeCode]
        }

        // Same as above
        if (_isEmpty(hotelPlanCodes) && hotelPlanCode) {
          hotelPlanCodes = [hotelPlanCode]
        }

        if (_isEmpty(roomPlanCodes) && roomPlanCode) {
          roomPlanCodes = [roomPlanCode]
        }

        const checkinDate = checkin || findDayAfterToday(this.defaultSearch.checkin_date_offset)
        const checkInDateParsed = parseDate(checkinDate, 'YYYY/MM/DD')
        const checkOutDateParsed = checkInDateParsed.add(0, 'day')
        const checkOutDateDayUse = formatDate02(checkOutDateParsed, 'YYYY/MM/DD', 'en')

        const checkoutDate = isDayUse ? checkOutDateDayUse : checkout || addDay(checkinDate, 1)
        let searchFormData = {
          checkin: checkinDate,
          checkout: checkoutDate,
          type,
          order,
          filter,
          is_day_use: isDayUse,
          mode,
          promo_code,
          roomTypeCodes,
          hotelPlanCodes,
          roomPlanCodes,
          is_including_occupied: false,
          utm_source,
          utm_medium,
          utm_campaign,
          medium,
          source,
          email_id,
          defineCheckInOnUrl: !!checkin,
          price_displayed_total,
          google_site,
          google_promo_code,
          currency
        }

        if (mode === 'secret' && hotelPlanCodes.length === 0) {
          searchFormData.mode = null
        }

        if (!this.isVoucherMode) {
          if (!_isNil(is_including_occupied)) {
            searchFormData.is_including_occupied = is_including_occupied === 'true'
          } else {
            searchFormData.is_including_occupied = !this.bookAvailableRoomsOnlyDefaultSetting
          }
        }

        if (coupon_code) {
          searchFormData.coupon_code = coupon_code
        }

        if (search_type === 'undated') {
          searchFormData.search_type = search_type
          searchFormData.nights = nights ? Number(nights) : 1
        }

        const rooms = buildRooms(this.$route.query.rooms, Number(adults) || undefined)

        if (this.isTiersMode && !this.$preview) {
          searchFormData = {
            ...searchFormData,
            adults: Number(adults) || rooms[0].adults,
            rooms: []
          }

          if (this.isMultiRoomSearchEnabled) {
            searchFormData.room_count = Number(room_count || 1)
          }

          if (kidsAgesList.length > 0) {
            const apiQuery = genKidsAgesTransferPayload({
              ...searchFormData,
              kidsAges: kidsAgesList
            })
            Hotels.getFacilities({
              hotel_codes: [this.hotelCode],
              is_including_lowest_price: false,
              rooms: apiQuery.rooms
            })
              .then((res) => {
                const kidsTiers = buildKidsTiers(
                  JSON.stringify(res.data[0].search_params.rooms[0].kids_age_range),
                  this.initialSetting
                )
                searchFormData.kidsTiers = kidsTiers
                if (kidsAgesList.length !== kidsTiers.reduce((acc, cur) => acc + cur.children, 0)) {
                  this.warning = 'kids'
                }
                resolve(searchFormData)
              })
              .catch(() => {
                searchFormData.kidsTiers = []
                resolve(searchFormData)
              })
          } else {
            const kidsTiers = buildKidsTiers(this.$route.query.kids_tiers, this.initialSetting)
            searchFormData.kidsTiers = kidsTiers
            resolve(searchFormData)
          }
        } else {
          searchFormData = {
            ...searchFormData,
            rooms: buildRooms(
              this.$route.query.rooms,
              Number(adults) || undefined,
              Number(children) || 0
            ),
            kidsTiers: null
          }
          resolve(searchFormData)
        }
      })
    },
    init() {
      this.assignQueryToSearchForm().then((searchFormData) => {
        this.search(searchFormData)
      })
    }
  }
}
</script>
